<template>
  <div
    ref="avatarElement"
    class="user-avatar"
    :class="sizeClass"
  >
    <BunnyImage
      :path="avatarPath"
      :fallback="avatarUrl"
      :alt="user?.name || user?.email"
      img-class="avatar-image"
    >
      <template #placeholder>
        <div
          v-if="user"
          class="avatar-initials"
          :style="{ backgroundColor: getUserColor(user?.email) }"
        >
          {{ initials }}
        </div>
        <div v-else class="avatar-unassigned">
          <font-awesome-icon :icon="['fal', 'user']" />
        </div>
      </template>
    </BunnyImage>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { getUserColor, getInitials } from '@/utils/userUtils';
import { useTooltip } from '@/composables/useTooltip';
import BunnyImage from '@/components/common/BunnyImage.vue';

const props = defineProps({
  user: {
    type: Object,
    default: null
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  tooltip: {
    type: String,
    default: null
  },
  noTooltip: {
    type: Boolean,
    default: false
  }
});

const avatarElement = ref(null);
const { createTooltip } = useTooltip();
let tooltipInstance = null;

const tooltipContent = computed(() => {
  return props.tooltip || props.user?.name || props.user?.email || 'Unassigned';
});

onMounted(() => {
  if (!props.noTooltip && avatarElement.value && tooltipContent.value) {
    tooltipInstance = createTooltip(avatarElement.value, {
      content: tooltipContent.value,
      placement: 'top'
    });
  }
});

watch(tooltipContent, (newContent) => {
  if (tooltipInstance && tooltipInstance.setContent) {
    tooltipInstance.setContent(newContent);
  }
});

const avatarUrl = computed(() => {
  if (!props.user) return null;
  return props.user.profile?.avatar || props.user.avatar || null;
});

const avatarPath = computed(() => {
  if (!props.user) return null;
  return props.user.profile?.avatarPath || props.user.avatarPath || null;
});

const initials = computed(() => {
  if (!props.user) return '';
  return getInitials(props.user.name || props.user.email);
});

const sizeClass = computed(() => {
  return `is-${props.size}`;
});
</script>

<style lang="scss" scoped>
.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  background: white;

  &.is-small {
    width: 24px;
    height: 24px;
    font-size: 10px;
  }

  &.is-medium {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  &.is-large {
    width: 48px;
    height: 48px;
    font-size: 16px;
  }

  .avatar-image,
  .avatar-initials,
  .avatar-unassigned {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .avatar-image {
    object-fit: cover;
  }

  .avatar-initials {
    color: white;
    font-weight: 600;
    text-transform: uppercase;
  }

  .avatar-unassigned {
    background: var(--bulma-grey-lighter);
    color: var(--bulma-grey);
  }
}
</style>